import { useEffect, useState } from "react";
import {
  Plug, CheckCircle2, AlertCircle, ExternalLink, Mail, Send,
  Trash2, Activity, Users, KeyRound, Zap, Copy, Info, Loader2, PartyPopper, Square, CheckSquare,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

/* Seller's Catalog API control plane. Lives inside Seller Settings →
   Integrations & API. Designed for the "old-schooler" — one big toggle,
   plain-English status banner, 3-step quick start, connected-buyers
   table, and a 1-click "Send setup invite" email to buyers.            */
export default function SellerCatalogApiPanel() {
  const [settings, setSettings] = useState(null);   // { enabled, grandfathered, usage:{...} }
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState(false);
  const [inviteFor, setInviteFor] = useState(null); // open Invite modal for this buyer
  const [showCustomInvite, setShowCustomInvite] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const refresh = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/seller/catalog-api/settings"),
        api.get("/seller/catalog-api/connections"),
      ]);
      setSettings(s.data);
      setConnections(c.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load API settings");
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggleEnabled = async (next) => {
    setBusy(true);
    try {
      const r = await api.put("/seller/catalog-api/settings", { enabled: next });
      if (r.data?.first_activation) {
        // First-ever activation — fire the celebration toast + auto-open
        // the broadcast modal so the seller can tell every buyer in one click.
        toast.success("🎉 You're live! Let's invite your buyers.", {
          description: "We'll email everyone connected to you in one go.",
          duration: 5000,
        });
        setBroadcastOpen(true);
      } else {
        toast.success(next ? "Catalog API enabled" : "Catalog API disabled");
      }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(false); }
  };

  if (!settings) {
    return <div className="p-8 text-center text-foreground/40 text-sm">
      <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…
    </div>;
  }

  const { enabled, usage = {} } = settings;
  const buyerPortalLink = `${window.location.origin}/buyer/settings#integrations`;

  return (
    <div className="space-y-5" data-testid="seller-catalog-api-panel">
      {/* ── Header + master switch ─────────────────────────────── */}
      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-xl bg-foreground text-background grid place-items-center shrink-0">
              <Plug className="w-5 h-5"/>
            </span>
            <div>
              <h2 className="display text-xl">Catalog API for your buyers</h2>
              <p className="text-[12.5px] text-foreground/65 mt-1 max-w-xl leading-relaxed">
                Turn this on and any of your connected buyers can pull your live catalog
                directly into their ERP, Shopify or accounting tool — no CSVs, no manual
                updates, no support tickets. You stay in full control: flip it off any time.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={toggleEnabled}
              disabled={busy}
              data-testid="seller-api-toggle"
              className="data-[state=checked]:bg-emerald-600"
            />
            <span className={`text-[10.5px] uppercase tracking-wider font-medium ${enabled ? "text-emerald-700" : "text-foreground/50"}`}>
              {enabled ? "On" : "Off"}
            </span>
          </div>
        </div>

        {/* Live status banner */}
        <div className={`mt-5 rounded-xl border p-4 text-[12.5px] flex items-start gap-3 ${
          enabled
            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : "bg-amber-50 border-amber-200 text-amber-900"
        }`} data-testid="seller-api-status-banner">
          {enabled ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>}
          <div className="flex-1">
            {enabled ? (
              <>
                <strong>Catalog API is live.</strong> {usage.buyers_connected || 0} buyer{usage.buyers_connected === 1 ? "" : "s"} connected · {usage.active_keys || 0} active key{usage.active_keys === 1 ? "" : "s"} · {(usage.requests_30d || usage.total_requests || 0).toLocaleString()} request{(usage.requests_30d || usage.total_requests) === 1 ? "" : "s"} {usage.requests_30d ? "in the last 30 days" : "all-time"}.
                {usage.last_activity_at && <span> Last activity {new Date(usage.last_activity_at).toLocaleString()}.</span>}
              </>
            ) : (
              <>
                <strong>Catalog API is paused.</strong> Your buyers can&apos;t pull your catalog into their systems right now. Existing API keys will resume working the moment you flip this back on — no buyer needs to regenerate anything.
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── 3-step quick-start — only when enabled ─────────────── */}
      {enabled && (
        <div className="rounded-2xl border border-border bg-secondary/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Get your buyers connected in 3 steps</h3>
            <span className="chip text-[10px]"><Zap className="w-3 h-3 mr-1"/>~2 min per buyer</span>
          </div>
          <ol className="grid sm:grid-cols-3 gap-3">
            <Step n={1} title="Share your portal link">
              Every buyer connected to you can self-serve. Send them this URL:
              <div className="mt-2 flex items-stretch gap-1">
                <code className="flex-1 mono text-[10.5px] bg-white border border-border rounded-md px-2 py-1.5 truncate" data-testid="buyer-portal-link">{buyerPortalLink}</code>
                <button onClick={() => { navigator.clipboard.writeText(buyerPortalLink); toast.success("Copied"); }}
                  className="px-2 rounded-md border border-border bg-white hover:bg-secondary/60" title="Copy">
                  <Copy className="w-3 h-3"/>
                </button>
              </div>
            </Step>
            <Step n={2} title="They click ‘Create API key’">
              Inside their Integrations &amp; API page, they generate a key scoped just to your catalog. Live or sandbox, their choice.
            </Step>
            <Step n={3} title="They plug it into Shopify / ERP">
              One-click Shopify OAuth, paste-into-Zapier, or drop it into their own ERP. Your catalog flows automatically every 6 hours.
            </Step>
          </ol>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => setBroadcastOpen(true)} data-testid="send-broadcast-invite"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 text-xs px-4">
              <Send className="w-3.5 h-3.5 mr-1.5"/>Tell every buyer at once
            </Button>
            <Button onClick={() => setShowCustomInvite(true)} variant="outline" data-testid="send-custom-invite"
              className="rounded-full h-9 text-xs px-4">
              <Mail className="w-3.5 h-3.5 mr-1.5"/>Email a specific buyer
            </Button>
            <a href="/buyer/settings#integrations" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-border text-xs hover:border-foreground/40">
              <ExternalLink className="w-3.5 h-3.5"/>Preview what your buyer sees
            </a>
          </div>
        </div>
      )}

      {/* ── Connected buyers table — only when enabled ─────────── */}
      {enabled && (
        <div className="rounded-2xl border border-border bg-white overflow-hidden">
          <div className="p-5 pb-3 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2"><Users className="w-4 h-4"/>Connected buyers</h3>
              <p className="text-[11.5px] text-foreground/55 mt-0.5">Buyers who&apos;ve created at least one API key against your catalog.</p>
            </div>
          </div>
          {connections.length === 0 ? (
            <div className="px-5 pb-6 text-center text-foreground/40 text-[12px]">
              <Activity className="w-5 h-5 mx-auto mb-1.5 opacity-50"/>
              No buyers have connected via the API yet. Share your portal link or email an invite above.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-secondary/40 text-[10.5px] uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="text-left px-5 py-2 font-medium">Buyer</th>
                  <th className="text-left px-3 py-2 font-medium">Keys</th>
                  <th className="text-left px-3 py-2 font-medium">Requests</th>
                  <th className="text-left px-3 py-2 font-medium">Last seen</th>
                  <th className="text-right px-5 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {connections.map(c => (
                  <tr key={c.buyer_id} className="border-t border-border" data-testid={`connection-row-${c.buyer_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{c.buyer_name || "Buyer"}</div>
                      {c.buyer_email && <div className="text-[10.5px] text-foreground/55">{c.buyer_email}</div>}
                    </td>
                    <td className="px-3 py-3">
                      <div className="mono text-[11px]">{c.key_count}</div>
                      <div className="text-[10.5px] text-foreground/55">
                        {c.live_keys > 0 && <span className="text-emerald-700">{c.live_keys} live</span>}
                        {c.live_keys > 0 && c.test_keys > 0 && " · "}
                        {c.test_keys > 0 && <span className="text-amber-700">{c.test_keys} test</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3 mono text-[11px]">{(c.requests || 0).toLocaleString()}</td>
                    <td className="px-3 py-3 text-[11px]">{c.last_used_at ? new Date(c.last_used_at).toLocaleString() : <span className="text-foreground/40">never</span>}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setInviteFor(c)}
                        className="text-[11px] text-foreground/65 hover:text-foreground mr-3 inline-flex items-center gap-1"
                        data-testid={`invite-buyer-${c.buyer_id}`}
                        title="Re-send setup instructions">
                        <Send className="w-3 h-3"/>Invite
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm(`Revoke all ${c.key_count} key${c.key_count === 1 ? "" : "s"} from ${c.buyer_name || "this buyer"}? They'll be locked out immediately.`)) return;
                        try {
                          await api.delete(`/seller/catalog-api/connections/${c.buyer_id}`);
                          toast.success("Keys revoked");
                          refresh();
                        } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
                      }}
                      className="text-[11px] text-rose-700 hover:text-rose-800 inline-flex items-center gap-1"
                      data-testid={`revoke-buyer-${c.buyer_id}`}>
                        <Trash2 className="w-3 h-3"/>Revoke all
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── How it works · plain-English explainer ─────────────── */}
      <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-5">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 mt-0.5 text-foreground/55 shrink-0"/>
          <div className="text-[12px] text-foreground/70 leading-relaxed space-y-1">
            <p><strong>What buyers can do with the API:</strong> read your product list, get live stock + prices, place orders against your catalog, and subscribe to webhooks when you update prices.</p>
            <p><strong>What buyers can&apos;t do:</strong> see your other buyers, change your prices, see your costs, or do anything you wouldn&apos;t do for them manually.</p>
            <p><strong>You stay in control:</strong> flip this off any time, revoke a single buyer&apos;s keys, or rate-limit specific keys from the buyer&apos;s side.</p>
          </div>
        </div>
      </div>

      {/* ── Invite modal — buyer-specific ──────────────────────── */}
      <InviteModal
        open={!!inviteFor}
        onClose={() => setInviteFor(null)}
        buyer={inviteFor}
      />
      <InviteModal
        open={showCustomInvite}
        onClose={() => setShowCustomInvite(false)}
        customMode
      />
      <BroadcastInviteModal
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
      />
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <li className="rounded-xl bg-white border border-border p-3.5">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="w-6 h-6 rounded-full bg-foreground text-background text-[11px] grid place-items-center font-medium">{n}</span>
        <span className="text-[12.5px] font-medium">{title}</span>
      </div>
      <div className="text-[11.5px] text-foreground/65 leading-relaxed">{children}</div>
    </li>
  );
}

function InviteModal({ open, onClose, buyer, customMode = false }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && buyer?.buyer_email) setEmail(buyer.buyer_email);
    if (!open) { setEmail(""); setNote(""); }
  }, [open, buyer]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/seller/catalog-api/send-invite", {
        buyer_id:    customMode ? null : buyer?.buyer_id,
        buyer_email: customMode ? email : null,
        note:        note || null,
      });
      toast.success("Setup invite emailed");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle>{customMode ? "Email setup instructions" : `Re-send setup invite to ${buyer?.buyer_name || "this buyer"}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {customMode && (
            <div>
              <Label>Buyer email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                placeholder="buyer@company.com" className="mt-1.5" data-testid="invite-email"/>
            </div>
          )}
          {!customMode && buyer?.buyer_email && (
            <div className="text-[12px] text-foreground/65">Sending to <span className="mono">{buyer.buyer_email}</span></div>
          )}
          <div>
            <Label>Personal note <span className="text-foreground/45 font-normal">(optional)</span></Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="invite-note"
              placeholder="Hey Jamie — we're now live on Verqo's Catalog API. Let me know if you need help wiring this up to your store."
              className="mt-1.5 w-full min-h-[90px] rounded-md border border-input bg-white px-3 py-2 text-sm"/>
          </div>
          <Button onClick={send} disabled={busy || (customMode && !email)}
            data-testid="invite-send-btn"
            className="w-full rounded-full bg-foreground text-background h-10">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-1.5"/>}
            Send setup email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Broadcast-to-all modal. Pre-loaded with every connected buyer; those
   already onboarded (active key) are marked with a chip and unchecked
   by default so the seller isn't spamming people who already plugged
   in. Sends in one shot via /seller/catalog-api/broadcast-invite. */
function BroadcastInviteModal({ open, onClose }) {
  const [buyers, setBuyers] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setNote("");
      return;
    }
    setLoading(true);
    api.get("/seller/catalog-api/all-buyers")
      .then(r => {
        const list = r.data?.data || [];
        setBuyers(list);
        // Pre-select everyone NOT already onboarded.
        setPicked(new Set(list.filter(b => !b.already_onboarded).map(b => b.buyer_id)));
      })
      .catch(e => toast.error(e.response?.data?.detail || "Couldn't load buyers"))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (bid) => {
    setPicked(prev => {
      const n = new Set(prev);
      if (n.has(bid)) n.delete(bid); else n.add(bid);
      return n;
    });
  };
  const allChecked  = buyers.length > 0 && buyers.every(b => picked.has(b.buyer_id));
  const noneChecked = picked.size === 0;
  const toggleAll = () => {
    if (allChecked) setPicked(new Set());
    else setPicked(new Set(buyers.map(b => b.buyer_id)));
  };

  const send = async () => {
    if (picked.size === 0) { toast.error("Pick at least one buyer"); return; }
    setSending(true);
    try {
      const r = await api.post("/seller/catalog-api/broadcast-invite", {
        buyer_ids: [...picked],
        note: note || null,
      });
      setResult(r.data);
      const okN = r.data?.sent_count || 0;
      const failN = r.data?.failed_count || 0;
      if (failN === 0) {
        toast.success(`Invited ${okN} buyer${okN === 1 ? "" : "s"} 🚀`);
      } else {
        toast.warning(`Sent to ${okN} of ${okN + failN}. ${failN} failed.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Broadcast failed"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="broadcast-invite-modal">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-emerald-600"/>Tell your buyers you&apos;re live
          </DialogTitle>
          <DialogDescription>
            One email, every connected buyer. They&apos;ll click through to set up the integration in 2 minutes.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 mt-2" data-testid="broadcast-result">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-900">
              <CheckCircle2 className="inline w-4 h-4 mr-1.5"/>
              Sent setup invites to <strong>{result.sent_count}</strong> buyer{result.sent_count === 1 ? "" : "s"}.
              {result.failed_count > 0 && (
                <span className="block mt-1 text-rose-700">{result.failed_count} email{result.failed_count === 1 ? "" : "s"} couldn&apos;t be delivered — we&apos;ll retry these automatically.</span>
              )}
            </div>
            <Button onClick={onClose} className="w-full rounded-full bg-foreground text-background h-10">Done</Button>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <p className="text-[12.5px] text-foreground/65">
              Send a one-click setup link to every buyer connected to you. Those already using the API are unchecked by default.
            </p>

            {loading ? (
              <div className="text-center py-8 text-foreground/45 text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading your buyers…
              </div>
            ) : buyers.length === 0 ? (
              <div className="text-center py-6 text-foreground/45 text-[13px]">
                <Users className="w-5 h-5 mx-auto mb-2 opacity-50"/>
                No connected buyers yet. Once buyers connect to your store, they&apos;ll show up here.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-[11px] text-foreground/60">
                  <button onClick={toggleAll} className="hover:text-foreground inline-flex items-center gap-1.5" data-testid="broadcast-toggle-all">
                    {allChecked ? <CheckSquare className="w-3.5 h-3.5"/> : <Square className="w-3.5 h-3.5"/>}
                    {allChecked ? "Deselect all" : "Select all"}
                  </button>
                  <span>{picked.size} of {buyers.length} selected</span>
                </div>

                <div className="rounded-lg border border-border max-h-[280px] overflow-y-auto divide-y divide-border" data-testid="broadcast-buyer-list">
                  {buyers.map(b => {
                    const isPicked = picked.has(b.buyer_id);
                    return (
                      <label key={b.buyer_id}
                        data-testid={`broadcast-buyer-${b.buyer_id}`}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/40 ${isPicked ? "bg-secondary/20" : ""}`}>
                        <input type="checkbox" checked={isPicked} onChange={() => toggle(b.buyer_id)}
                          className="w-4 h-4 accent-foreground"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12.5px] font-medium truncate">{b.buyer_name || "Buyer"}</div>
                          <div className="text-[10.5px] text-foreground/50 truncate">{b.buyer_email}</div>
                        </div>
                        {b.already_onboarded && (
                          <span className="chip bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9.5px]">already using</span>
                        )}
                      </label>
                    );
                  })}
                </div>

                <div>
                  <Label>Personal note <span className="text-foreground/45 font-normal">(optional)</span></Label>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="broadcast-note"
                    placeholder="Hey — we just turned on the Catalog API on Verqo. Plug us into Shopify or your ERP in 2 minutes. Let me know if you need anything."
                    className="mt-1.5 w-full min-h-[80px] rounded-md border border-input bg-white px-3 py-2 text-sm"/>
                </div>

                <Button onClick={send} disabled={sending || noneChecked || loading}
                  data-testid="broadcast-send-btn"
                  className="w-full rounded-full bg-foreground text-background h-10">
                  {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-1.5"/>}
                  Send to {picked.size} buyer{picked.size === 1 ? "" : "s"}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

